import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag, Loader2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function Cart() {
  const { items, remove, setQty, total, clear } = useCart();
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const checkout = async () => {
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/payments/checkout", payload);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-2xl px-5 py-28 text-center">
        <ShoppingBag className="mx-auto text-zinc-700" size={48} />
        <h1 className="mt-6 font-display text-2xl font-800 text-white">Your cart is empty</h1>
        <p className="mt-2 text-zinc-500">Find something rare to add.</p>
        <Link to="/catalog" data-testid="cart-empty-shop" className="mt-8 inline-flex rounded-full bg-primary px-7 py-3.5 font-medium text-white transition-transform hover:-translate-y-1">Browse cards</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-5 py-14 sm:px-8">
      <h1 className="font-display text-3xl font-900 tracking-tighter text-white sm:text-4xl">Your Cart</h1>
      <div className="mt-10 grid gap-10 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2" data-testid="cart-items">
          {items.map((i) => (
            <div key={i.id} data-testid={`cart-item-${i.id}`} className="flex gap-4 rounded-2xl border border-white/10 bg-card p-4">
              <img src={i.image} alt={i.name} className="h-24 w-20 rounded-lg object-cover" />
              <div className="flex flex-1 flex-col">
                <h3 className="font-display font-700 text-white">{i.name}</h3>
                <span className="text-sm text-zinc-500">${i.price.toFixed(2)}</span>
                <div className="mt-auto flex items-center gap-3">
                  <div className="flex items-center rounded-full border border-white/10">
                    <button onClick={() => setQty(i.id, i.quantity - 1)} className="p-2 text-zinc-400 hover:text-white"><Minus size={14} /></button>
                    <span className="w-7 text-center text-sm text-white">{i.quantity}</span>
                    <button onClick={() => setQty(i.id, i.quantity + 1)} className="p-2 text-zinc-400 hover:text-white"><Plus size={14} /></button>
                  </div>
                  <button onClick={() => remove(i.id)} data-testid={`remove-${i.id}`} className="text-zinc-500 transition-colors hover:text-red-400"><Trash2 size={16} /></button>
                </div>
              </div>
              <div className="font-display font-800 text-white">${(i.price * i.quantity).toFixed(2)}</div>
            </div>
          ))}
          <button onClick={clear} className="text-sm text-zinc-500 hover:text-white">Clear cart</button>
        </div>

        <div className="h-fit rounded-2xl border border-white/10 bg-card p-6">
          <h2 className="font-display text-lg font-700 text-white">Order summary</h2>
          <div className="mt-4 space-y-2 text-sm text-zinc-400">
            <div className="flex justify-between"><span>Subtotal</span><span className="text-white" data-testid="cart-subtotal">${total.toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Tax</span><span>Calculated at checkout</span></div>
            <div className="flex justify-between"><span>Shipping</span><span>Calculated at checkout</span></div>
          </div>
          <div className="mt-4 flex justify-between border-t border-white/10 pt-4 font-display text-lg font-800 text-white">
            <span>Total</span><span>${total.toFixed(2)}</span>
          </div>
          <button onClick={checkout} disabled={loading} data-testid="checkout-btn"
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-7 py-3.5 font-medium text-white transition-transform hover:-translate-y-1 disabled:opacity-60">
            {loading ? <><Loader2 className="animate-spin" size={18} /> Redirecting…</> : "Checkout"}
          </button>
          <p className="mt-3 text-center text-xs text-zinc-600">Secure payment via Stripe</p>
        </div>
      </div>
    </div>
  );
}
